import TextView from "../../TextView"
import styled from 'styled-components';
import MastercardIcon from "assets/images/settings/mastercard.svg";
import VisaIcon from "assets/images/settings/visa.svg";
import JCBIcon from "assets/images/settings/jcb.svg";
import AmericanExpressIcon from "assets/images/settings/americanExpress.svg";
import DiscoverIcon from "assets/images/settings/discover.svg";
import DinersClubIcon from "assets/images/settings/dinersClub.svg";
import UnionPayIcon from "assets/images/settings/unionPay.svg";
import MaestroIcon from "assets/images/settings/maestro.svg";
import GenericCardIcon from "assets/images/settings/genericCard.svg";
import { colors } from "../../../utils";
import { StyledSubscriptionRowItem } from "./styles";
import { format } from "date-fns";
import { parseLocalDate } from "../../../utils/safeDate";

export const Row = styled.div`
    display: flex;
    gap: 8px;
    margin-left: ${props => props.moreSpacing ? "30px" : "10px"};
`

export const cardIcon = {
  "visa": VisaIcon,
  "mastercard": MastercardIcon,
  "jcb": JCBIcon,
  "amex": AmericanExpressIcon,
  "discover": DiscoverIcon,
  "diners": DinersClubIcon,
  "unionpay": UnionPayIcon,
  "maestro": MaestroIcon,
}

// Neutral card icon used whenever a brand has no dedicated logo, so the UI
// never renders a broken/empty image regardless of the card brand returned.
export const fallbackCardIcon = GenericCardIcon;

// Returns the logo for a card brand, falling back to a generic card icon for
// any unknown/new brand (brands are normalized to lowercase to be safe).
export const getCardIcon = (brand) =>
  cardIcon[String(brand || "").toLowerCase()] || fallbackCardIcon;

// Returns a human-readable brand name, capitalizing unknown brands and
// defaulting to "Card" when the brand is missing.
export const getCardName = (brand) => {
  const key = String(brand || "").toLowerCase();
  return (
    cardNames[key] ||
    (key ? key.charAt(0).toUpperCase() + key.slice(1) : "Card")
  );
};

// Billing History columns.
// sortable is intentionally false on every column: the shared <Table> drives
// react-data-table with sortServer={true}, so clicking a header only wrote
// order_by/order_direction URL params — but the transactions fetch
// (getAllTransactions, server-paginated) never consumes them, so the header
// toggled its arrow without ever re-sorting. Rather than ship a dead affordance,
// the sort UI is removed until real server-side sort is wired for this endpoint.
// T22.243 — what a Payment history row was FOR. The list now carries every
// charge, not only SUBSCRIPTION rows, so the label can't just be sub_package
// (an unlock or refund has none and rendered an empty pill).
export const describeTransaction = (row) => {
  const purpose = row?.transaction_purpose;
  const tf = row?.transaction_for || {};
  const plan = tf.sub_package;
  if (purpose === "UNLOCK" || purpose === "WHEEL") {
    return tf.wheel_name ? `Unlock: ${tf.wheel_name}` : "Wheel unlock";
  }
  if (purpose === "REFUND") {
    return tf.refund_of ? `Refund: ${tf.refund_of}` : "Refund";
  }
  if (purpose === "SUBSCRIPTION_UPGRADE") {
    return plan ? `Upgrade to ${plan}` : "Plan upgrade";
  }
  if (tf.billing_reason === "subscription_cycle") {
    const base = plan ? `${plan} renewal` : "Plan renewal";
    return Number(tf.seat_amount) > 0 ? `${base} + seats` : base;
  }
  return plan || "";
};

const transactionAriaLabel = (row) => {
  const label = describeTransaction(row);
  if (!label) return undefined;
  return label === row?.transaction_for?.sub_package ? `${label} plan` : label;
};

// "-12.50" (refund) -> "-$12.50"; "15.00" -> "$15.00".
export const formatCharge = (amount) => {
  const n = parseFloat(amount);
  if (!Number.isFinite(n)) return "-";
  return n < 0 ? `-$${Math.abs(n).toFixed(2)}` : `$${n.toFixed(2)}`;
};

export const paymentTableColumns = [
  {
    name: "Date",
    // parseLocalDate: a date-only "YYYY-MM-DD" is UTC-parsed by `new Date(...)` and displays the
    // previous calendar day for every Americas viewer (settings-account §15.7 / recut of #3649).
    // parseISO on sandbox already did the same calendar-day pin; this routes through the shared
    // helper so the cross-engine guard can prove the class cannot silently return.
    selector: (row) => {
      const d = row?.date ? parseLocalDate(row.date) : null;
      return <TextView>{d ? format(d, "MM/dd/yy") : "-"}</TextView>;
    },
    sortable: false,
    sortField: "date",
  },
  {
    name: "Payment Method",
    selector: (row) => (
      <Row moreSpacing={row?.payment_method ? false : true}>
        {row?.payment_method ? (
          <>
            <img src={getCardIcon(row?.payment_method?.brand)} alt={getCardName(row?.payment_method?.brand)} width={24.5} height={16} />
            <TextView>{row?.payment_method.last4}</TextView>
          </>
        ) : (
          <TextView>-</TextView>
        )}
      </Row>
    ),
    sortable: false,
    sortField: "payment_method",
  },
  {
    name: "Transaction",
    selector: (row) => (
      <StyledSubscriptionRowItem
        backgroundColor={
          subscriptionColors[row?.transaction_for?.sub_package]
            ?.backgroundColor || "transparent"
        }
        textColor={
          subscriptionColors[row?.transaction_for?.sub_package]?.textColor ||
          colors.darkGrey
        }
        aria-label={transactionAriaLabel(row)}
      >
        {describeTransaction(row)}
      </StyledSubscriptionRowItem>
    ),
    sortable: false,
    sortField: "transaction_purpose",
  },
  {
    name: "Charge",
    selector: (row) => <TextView>{formatCharge(row?.amount)}</TextView>,
    sortable: false,
    sortField: "amount",
  },
];

export const filterOptions = [
    {
      label: "All Time",
      value: "all",
    },
    {
      label: "By Date Range",
      value: "date",
    },
];

export const durationOptions = {
    10: "Month",
    40: "Year",
};

export const durationDisplay = {
  10: "Monthly",
  40: "Annually",
};

export const cardNames = {
  "visa": "Visa Card",
  "mastercard": "MasterCard",
  "jcb": "JCB Card",
  "amex": "American Express Card",
  "discover": "Discover Card",
  "diners": "Diners Club",
  "unionpay": "UnionPay Card",
  "maestro": "Maestro Card",
}

export const subscriptionColors = {
    "Essential": {
        backgroundColor: colors.yellowLighter,
        textColor: colors.yellowText
    },
    "Premium": {
        backgroundColor: colors.purpleLighter,
        textColor: colors.purpleText
    },
    "ProSuite": {
        backgroundColor: colors.greenLighter,
        textColor: colors.greenText
    }
}